import { defer, MonoTypeOperatorFunction, Observable, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';

export interface RetryWhileBusyConfig {
  maxDurationMs: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  now?: () => number;
  random?: () => number;
}

const BUSY_STATUS = 429;
const RETRY_AFTER_JITTER_RATIO = 0.5;

export function retryWhileBusy<T>(config: RetryWhileBusyConfig): MonoTypeOperatorFunction<T> {
  const baseDelayMs = config.baseDelayMs ?? 2000;
  const maxDelayMs = config.maxDelayMs ?? 30000;
  const now = config.now ?? (() => Date.now());
  const random = config.random ?? Math.random;

  return (source: Observable<T>) =>
    defer(() => {
      const deadline = now() + config.maxDurationMs;
      return source.pipe(
        retry({
          delay: (error, retryCount) => {
            if (!isBusy(error)) {
              return throwError(() => error);
            }
            const delayMs = retryDelayMs(error, retryCount, baseDelayMs, maxDelayMs, random);
            if (now() + delayMs > deadline) {
              return throwError(() => error);
            }
            return timer(delayMs);
          },
        }),
      );
    });
}

export function isBusy(error: any): boolean {
  return error?.status === BUSY_STATUS;
}

function retryDelayMs(error: any, retryCount: number, baseDelayMs: number, maxDelayMs: number, random: () => number): number {
  const retryAfterMs = retryAfterHeaderMs(error);
  if (retryAfterMs !== null) {
    return Math.round(retryAfterMs * (1 + random() * RETRY_AFTER_JITTER_RATIO));
  }
  const exponential = Math.min(baseDelayMs * 2 ** (retryCount - 1), maxDelayMs);
  return Math.round(random() * exponential);
}

function retryAfterHeaderMs(error: any): number | null {
  const header = error?.headers?.get?.('Retry-After');
  if (!header) {
    return null;
  }
  const seconds = Number(header);
  return Number.isFinite(seconds) ? seconds * 1000 : null;
}
